import { Link } from "react-router-dom";
import { Sparkles, CheckCircle2, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { BrowserFrame, SCREEN_MAP, SCREEN_IMAGES } from "@/components/site/mockups";

export const Label = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-teal-700 bg-teal-50 px-3 py-1 rounded-full border border-teal-200/60">
    <Sparkles className="w-3 h-3" /> {children}
  </span>
);

export const LabelDark = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-teal-300 bg-white/5 border border-white/10 px-3 py-1 rounded-full">
    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {children}
  </span>
);

export const SectionHead = ({ eyebrow, title, desc, center = false, className = "" }) => (
  <Reveal className={`${center ? "text-center mx-auto" : ""} max-w-2xl ${className}`}>
    {eyebrow && <Label>{eyebrow}</Label>}
    <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 text-slate-900">{title}</h2>
    {desc && <p className="mt-4 text-lg text-slate-600 leading-relaxed">{desc}</p>}
  </Reveal>
);

export const PointList = ({ points, dark = false }) => (
  <ul className="mt-6 space-y-3">
    {points.map((p) => (
      <li key={p} className={`flex items-start gap-2.5 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>
        <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${dark ? "text-emerald-400" : "text-teal-500"}`} />
        {p}
      </li>
    ))}
  </ul>
);

/* Two-column feature block with a product screenshot inside a BrowserFrame.
   Pass `image` + `alt` to drop in a REAL AYOBELL screenshot; otherwise the
   temporary CSS mockup for `screenKey` is used as a placeholder. */
export function ScreenBlock({ eyebrow, title, desc, points = [], screenKey, image, alt, url, reverse = false }) {
  const Screen = SCREEN_MAP[screenKey]?.Comp;
  const realImg = image || SCREEN_IMAGES[screenKey]?.src;
  const realAlt = alt || SCREEN_IMAGES[screenKey]?.alt || `${title} — AYOBELL`;
  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
      <Reveal className={reverse ? "lg:order-2" : ""}>
        {eyebrow && <Label>{eyebrow}</Label>}
        <h3 className="font-display font-bold text-2xl sm:text-3xl mt-4 text-slate-900 tracking-tight">{title}</h3>
        {desc && <p className="mt-4 text-slate-600 leading-relaxed">{desc}</p>}
        {points.length > 0 && <PointList points={points} />}
      </Reveal>
      <Reveal delay={0.12} className={reverse ? "lg:order-1" : ""}>
        <BrowserFrame image={realImg} alt={realAlt} url={url}>
          {Screen ? <Screen /> : null}
        </BrowserFrame>
      </Reveal>
    </div>
  );
}

export const Pill = ({ children, dark = false }) => (
  <span className={`text-xs font-medium px-3 py-1.5 rounded-full ${dark ? "text-teal-100 bg-white/5 border border-white/10" : "text-slate-600 bg-slate-100"}`}>{children}</span>
);

export function CTABand({ title = "See AYOBELL in action for your service.", desc = "Book a personalised demo and we'll show you how AYOBELL fits your care model, teams and governance processes.", primaryTo = "/contact", primaryLabel = "Book a Demo", secondaryTo = "/pricing", secondaryLabel = "Talk to our team" }) {
  return (
    <section className="bg-white py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-slate-900 text-white px-6 sm:px-12 py-14 sm:py-16 text-center">
            <div className="absolute inset-0 ayo-grid-bg-dark opacity-40" />
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] rounded-full bg-teal-500/20 blur-[120px]" />
            <div className="relative">
              <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-3xl mx-auto leading-tight">{title}</h2>
              <p className="mt-4 text-slate-300 max-w-xl mx-auto">{desc}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to={primaryTo} data-testid="cta-band-primary" className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-emerald-500 px-7 py-4 rounded-full hover:shadow-xl hover:shadow-emerald-500/30 hover:scale-[1.03] active:scale-[0.98] transition-all">
                  {primaryLabel} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to={secondaryTo} data-testid="cta-band-secondary" className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-white/5 border border-white/15 px-7 py-4 rounded-full hover:bg-white/10 transition-all">
                  {secondaryLabel}
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
